import { Group, Stack } from "@mantine/core"
import { keepPreviousData } from "@tanstack/react-query"
import { ColumnManager, InfoBox, Loader } from "@tfg/ui"
import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"

import { TABLE_CONTEXT_MAP } from "@tfg/types/schemas/modules/view/constants/table-context-map"

import { SortBuilder } from "~/components/SortBuilder"
import { CustomTable } from "~/components/table/CustomTable"
import { FilterBuilderWrapper } from "~/components/table/FilterBuilderWrapper"
import { SearchBar } from "~/components/table/SearchBar"
import { ViewHeader } from "~/components/table/views/ViewHeader"
import { Views } from "~/components/table/views/Views"
import {
  FindEcoModulationProductsQueryResponse,
  FindEcoModulationProductsRequestFiltersDto,
  useFindEcoModulationProducts,
} from "~/gen/api"
import { useGetColumnManagerTranslations } from "~/hooks/table"
import { usePaginatedQuery } from "~/hooks/usePaginatedQuery"
import { useBuildEcoModulationSorts } from "~/pages/Brand/Regulations/EcoModulations/useBuildEcoModulationSorts"
import { useEcoModulationTable } from "~/pages/Brand/Regulations/EcoModulations/useEcoModulationTable"
import { useBuildProductFilters } from "~/pages/Brand/Products/List/hooks/useBuildProductFilters"

export const ProductsEcoModulationsTable = () => {
  const [isColumnManagerOpen, setIsColumnManagerOpen] = useState(false)
  const navigate = useNavigate()
  const { t } = useTranslation(["regulations", "products"])
  const columnManagerTranslations = useGetColumnManagerTranslations()

  const ecoModExtraFilters = useMemo(() => {
    return [
      {
        label: t("regulations:ecoModulations.filters.status"),
        key: "ecoModulationStatus" as const,
        type: "multiSelect" as const,
        options: [
          {
            value: "no-data",
            label: t("regulations:ecoModulations.filters.noData"),
          },
          {
            value: "any-fired",
            label: t("regulations:ecoModulations.filters.anyFired"),
          },
          {
            value: "any-bonus",
            label: t("regulations:ecoModulations.filters.anyBonus"),
          },
          {
            value: "any-malus",
            label: t("regulations:ecoModulations.filters.anyMalus"),
          },
        ],
      },
      {
        label: t("regulations:ecoModulations.filters.datapoint"),
        key: "ecoModulationDatapoints" as const,
        type: "multiSelect" as const,
        options: [
          {
            value: "BONUS_DURABILITY",
            label: t("regulations:ecoModulations.columns.bonusDurability"),
          },
          {
            value: "BONUS_ENVIRONMENTAL_LABELS",
            label: t(
              "regulations:ecoModulations.columns.bonusEnvironmentalLabels"
            ),
          },
          {
            value: "BONUS_RECYCLED_MATERIALS",
            label: t(
              "regulations:ecoModulations.columns.bonusRecycledMaterials"
            ),
          },
          {
            value: "MALUS_METALLOPLASTIC_FIBERS",
            label: t(
              "regulations:ecoModulations.columns.malusMetalloplasticFibers"
            ),
          },
          {
            value: "MALUS_EEE_COMPONENTS",
            label: t("regulations:ecoModulations.columns.malusEeeComponents"),
          },
        ],
      },
    ]
  }, [t])

  const { title, searchPlaceholder, options, filtersValue, setFiltersValue } =
    useBuildProductFilters<FindEcoModulationProductsRequestFiltersDto>(
      ecoModExtraFilters
    )

  const { sorts, setSorts, sortOptions } = useBuildEcoModulationSorts()

  const {
    data: productsData,
    isPending: isLoadingProducts,
    setPageBasedPagination,
    pageBasedPagination,
    totalPages,
  } = usePaginatedQuery<
    typeof useFindEcoModulationProducts,
    FindEcoModulationProductsQueryResponse
  >(
    useFindEcoModulationProducts,
    {
      filters: filtersValue,
      sorts,
    },
    {
      query: {
        placeholderData: keepPreviousData,
      },
    }
  )

  const table = useEcoModulationTable({
    pagination: pageBasedPagination,
    setPagination: setPageBasedPagination,
    pageCount: totalPages,
    products: productsData?.data ?? [],
  })

  const isEmpty = !isLoadingProducts && (productsData?.count ?? 0) === 0

  return (
    <Stack>
      <Views tableContext={TABLE_CONTEXT_MAP.ECO_MODULATIONS} />
      <Group>
        <SearchBar
          filtersValue={filtersValue}
          setFiltersValue={setFiltersValue}
          placeholder={t("products:lists.placeholders.searchProduct")}
          isFetching={isLoadingProducts}
        />
        <SortBuilder sorts={sorts} setSorts={setSorts} options={sortOptions} />
        <FilterBuilderWrapper<FindEcoModulationProductsRequestFiltersDto>
          title={title}
          searchPlaceholder={searchPlaceholder}
          options={options}
          filters={filtersValue}
          setFilters={setFiltersValue}
        />
        <ColumnManager
          table={table}
          translations={columnManagerTranslations}
          isOpen={isColumnManagerOpen}
          setIsOpen={setIsColumnManagerOpen}
        />
        <ViewHeader tableContext={TABLE_CONTEXT_MAP.ECO_MODULATIONS} />
      </Group>
      {isEmpty ? (
        <InfoBox
          title={t("regulations:ecoModulations.emptyState.title")}
          message={t("regulations:ecoModulations.emptyState.message")}
        />
      ) : !isLoadingProducts ? (
        <CustomTable
          table={table}
          onRowClick={(product) => navigate(`/products/details/${product.id}`)}
          pagination={true}
          manualPaginationCount={productsData?.count}
          layout="fixed"
          setOpenColumnManager={setIsColumnManagerOpen}
          stickyHeader
        />
      ) : (
        <Loader fullPage />
      )}
    </Stack>
  )
}
